// src/components/Control/robot/cameraFeed.ts
// Кадры камер (robot/<id>/cams/0, cams/1 — JPEG) с одной подпиской на камеру,
// сколько бы окон её ни показывали (превью, полноэкранный режим, «Няня»).
// Подписка живёт, пока камеру показывает хоть одно окно (+ RELEASE_MS, чтобы
// смена раскладки не переподписывала).
import { useEffect, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';

const RELEASE_MS = 2000;

interface FeedState {
  url: string | null;
  at: number;
  fps: number;
  error: string | null;
}

class Feed {
  state: FeedState = { url: null, at: 0, fps: 0, error: null };
  private listeners = new Set<(s: FeedState) => void>();
  private sub: zenoh.Subscriber | null = null;
  private refs = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private arrivals: number[] = [];
  private closed = false;
  private opening = false;
  blob: Blob | null = null;   // последний кадр как есть — для снимка в браузер

  constructor(private session: zenoh.Session, private key: string) {}

  acquire(listener: (s: FeedState) => void) {
    this.listeners.add(listener);
    this.refs++;
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    if (!this.sub && !this.opening && !this.closed) this.open();
    listener(this.state);
  }

  release(listener: (s: FeedState) => void) {
    this.listeners.delete(listener);
    this.refs--;
    if (this.refs <= 0 && !this.releaseTimer) {
      this.releaseTimer = setTimeout(() => this.close(), RELEASE_MS);
    }
  }

  private emit(patch: Partial<FeedState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach(l => l(this.state));
  }

  private open() {
    this.closed = false;
    this.opening = true;
    this.session
      .declareSubscriber(this.key, {
        handler: (sample: zenoh.Sample) => {
          const bytes = sample.payload().toBytes();
          this.blob = new Blob([bytes as BlobPart], { type: 'image/jpeg' });
          const url = URL.createObjectURL(this.blob);
          const prev = this.state.url;
          const now = Date.now();
          this.arrivals.push(now);
          while (this.arrivals.length && now - this.arrivals[0] > 2000) this.arrivals.shift();
          this.emit({ url, at: now, fps: this.arrivals.length / 2, error: null });
          // Предыдущий кадр уже нарисован — освобождаем чуть позже, чтобы <img> успели сменить src
          if (prev) setTimeout(() => URL.revokeObjectURL(prev), 1000);
        },
      })
      .then(sub => {
        this.opening = false;
        if (this.closed) sub.undeclare().catch(() => {});
        else this.sub = sub;
      })
      .catch(err => {
        this.opening = false;
        this.emit({ error: err instanceof Error ? err.message : String(err) });
      });
  }

  private close() {
    this.releaseTimer = null;
    if (this.refs > 0) return;
    this.closed = true;
    this.sub?.undeclare().catch(() => {});
    this.sub = null;
    if (this.state.url) URL.revokeObjectURL(this.state.url);
    this.state = { url: null, at: 0, fps: 0, error: null };
    this.blob = null;
    feeds.get(this.session)?.delete(this.key);
  }
}

const feeds = new WeakMap<zenoh.Session, Map<string, Feed>>();

function feedFor(session: zenoh.Session, key: string): Feed {
  let m = feeds.get(session);
  if (!m) {
    m = new Map();
    feeds.set(session, m);
  }
  let f = m.get(key);
  if (!f) {
    f = new Feed(session, key);
    m.set(key, f);
  }
  return f;
}

// Последний пришедший кадр камеры (JPEG), если её сейчас показывает хоть одно окно
export function latestFrame(session: zenoh.Session, key: string): { blob: Blob; at: number } | null {
  const f = feeds.get(session)?.get(key);
  return f?.blob ? { blob: f.blob, at: f.state.at } : null;
}

// Последний кадр камеры; key = null — не подписываться (камера скрыта)
export function useCameraFeed(session: zenoh.Session | null, key: string | null): FeedState {
  const [state, setState] = useState<FeedState>({ url: null, at: 0, fps: 0, error: null });
  useEffect(() => {
    setState({ url: null, at: 0, fps: 0, error: null });
    if (!session || !key) return;
    const feed = feedFor(session, key);
    const listener = (s: FeedState) => setState(s);
    feed.acquire(listener);
    return () => feed.release(listener);
  }, [session, key]);
  return state;
}
